import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa6";
import "./SignInModal.css";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(value) {
  return EMAIL_PATTERN.test(value.trim());
}

function EmailValidIcon() {
  return (
    <svg
      className="sign-in-modal__valid-icon"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="9" cy="9" r="8" stroke="#031f36" strokeWidth="1.2" />
      <path
        d="M5.5 9.2L7.8 11.5L12.5 6.8"
        stroke="#031f36"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function SignInModal({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(true);

  const emailIsValid = isValidEmail(email);

  useEffect(() => {
    if (!open) return undefined;

    setEmail("");
    setName("");
    setSurname("");
    setPassword("");
    setShowPassword(false);

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="sign-in-modal" role="presentation" onClick={onClose}>
      <div
        className="sign-in-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sign-in-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="sign-in-modal__close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <h2 id="sign-in-modal-title">Specify account details</h2>
        <p className="sign-in-modal__intro">
          You must fill out the information below to complete your membership.
        </p>

        <form className="sign-in-modal__form" onSubmit={(event) => event.preventDefault()}>
          <label className="sign-in-modal__field">
            <span>E-Mail address</span>
            <div className="sign-in-modal__input-wrap">
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Enter your email address"
                autoComplete="email"
              />
              {emailIsValid && (
                <span className="sign-in-modal__valid" aria-hidden="true">
                  <EmailValidIcon />
                </span>
              )}
            </div>
          </label>

          <div className="sign-in-modal__row">
            <label className="sign-in-modal__field">
              <span>Name</span>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Enter your first name"
                autoComplete="given-name"
              />
            </label>
            <label className="sign-in-modal__field">
              <span>Surname</span>
              <input
                type="text"
                value={surname}
                onChange={(event) => setSurname(event.target.value)}
                placeholder="Enter your surname"
                autoComplete="family-name"
              />
            </label>
          </div>

          <label className="sign-in-modal__field">
            <span>Password</span>
            <div className="sign-in-modal__input-wrap">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Create a password"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="sign-in-modal__toggle"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </label>

          <p className="sign-in-modal__hint">
            <span aria-hidden="true">ⓘ</span>
            Your password must be at least 8 characters long and contain at least one letter,
            number or special character.
          </p>

          <label className="sign-in-modal__terms">
            <input
              type="checkbox"
              className="sign-in-modal__checkbox"
              checked={acceptedTerms}
              onChange={(event) => setAcceptedTerms(event.target.checked)}
            />
            <span>
              By creating an account, accessing, or using the Highwatch platform, you agree to our{" "}
              <Link to="/TermsOfService" onClick={onClose}>
                Terms of Service
              </Link>
              ,{" "}
              <Link to="/PrivacyPolicy" onClick={onClose}>
                Privacy Policy
              </Link>
              , and{" "}
              <Link to="/PrivacyPolicy" onClick={onClose}>
                Cookie Policy
              </Link>
              . You acknowledge that Highwatch provides AI-powered workflow assistance and that
              human review remains your responsibility for regulated decisions.
            </span>
          </label>

          <button type="submit" className="sign-in-modal__submit" disabled={!acceptedTerms}>
            Register
          </button>
        </form>
      </div>
    </div>
  );
}
